import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useColorMode } from '@chakra-ui/react';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { APPEARANCE_STORAGE_KEY, type AppearanceIntent, type ResolvedAppearance } from './constants';
import { resolveAppearance } from './resolveAppearance';
import { readIntent, readPrefersDark, writeIntent } from './colorModeManager';

export interface AppearanceContextValue {
  intent: AppearanceIntent;
  resolved: ResolvedAppearance;
  setIntent: (intent: AppearanceIntent) => void;
}

export const AppearanceContext = createContext<AppearanceContextValue | undefined>(undefined);

/**
 * Registers exactly one `prefers-color-scheme` "change" listener, supporting
 * both the modern `addEventListener`/`removeEventListener` MediaQueryList API
 * and the legacy `addListener`/`removeListener` pair some WebViews still use.
 */
function subscribeToSystemPreference(onChange: (matches: boolean) => void): () => void {
  let mql: MediaQueryList;
  try {
    mql = window.matchMedia('(prefers-color-scheme: dark)');
  } catch {
    return () => {};
  }
  const listener = (event: MediaQueryListEvent) => onChange(event.matches);
  if (typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', listener);
    return () => mql.removeEventListener('change', listener);
  }
  mql.addListener(listener);
  return () => mql.removeListener(listener);
}

/**
 * Keeps the native status bar text style in sync with resolved appearance
 * (D45a Phase 1 hardening item). No-op on web — `@capacitor/status-bar` is
 * only meaningful inside the iOS/Android WebView.
 */
function syncNativeStatusBar(resolved: ResolvedAppearance): void {
  if (!Capacitor.isNativePlatform()) return;
  StatusBar.setStyle({ style: resolved === 'dark' ? Style.Dark : Style.Light }).catch(() => {
    // Best-effort — a status bar sync failure must never block appearance switching.
  });
}

/**
 * Always-mounted root (d45_mode_wiring_decision.md §4 item 4, plan Phase 1
 * round-2 fix F8) — owns the single System `matchMedia` listener so live
 * tracking survives the mount/unmount of any individual consumer (e.g. the
 * Settings screen). Mount this alongside `ChakraProvider` in main.tsx, not
 * nested inside any feature-level consumer.
 */
export function AppearanceProvider({ children }: { children: ReactNode }) {
  const [intent, setIntentState] = useState<AppearanceIntent>(() => readIntent());
  const [prefersDark, setPrefersDark] = useState<boolean>(() => readPrefersDark());
  const { setColorMode } = useColorMode();

  useEffect(() => subscribeToSystemPreference(setPrefersDark), []);

  const resolved = useMemo(() => resolveAppearance(intent, prefersDark), [intent, prefersDark]);

  useEffect(() => {
    syncNativeStatusBar(resolved);
  }, [resolved]);

  // Live sync: whenever `resolved` changes for any reason — including a
  // System-flip observed by the matchMedia listener above, which has no
  // other write path to Chakra — stamp it to `html[data-theme]`. This is
  // still the single write path: `applyIntent` mutates `resolved` (via
  // `intent`/`prefersDark`) rather than writing to Chakra a second time
  // with independent logic, so this effect just closes the loop for
  // updates `applyIntent` didn't initiate.
  useEffect(() => {
    setColorMode?.(resolved);
  }, [resolved, setColorMode]);

  const applyIntent = useCallback(
    (nextIntent: AppearanceIntent, persist: boolean) => {
      const nextResolved = resolveAppearance(nextIntent, readPrefersDark());
      if (persist) {
        writeIntent(nextIntent);
      }
      setIntentState(nextIntent);
      // Single synchronous write path: DOM (via Chakra's dataset/class stamp)
      // and React context change together, so they can never diverge.
      setColorMode?.(nextResolved);
    },
    [setColorMode]
  );

  const setIntent = useCallback((nextIntent: AppearanceIntent) => applyIntent(nextIntent, true), [
    applyIntent,
  ]);

  // Cross-tab sync decision: a mode change in one tab propagates live to
  // other open tabs via the standard `storage` event (fired on every tab
  // except the one that wrote the key), applied without re-persisting.
  useEffect(() => {
    function handleStorage(event: StorageEvent) {
      if (event.key !== APPEARANCE_STORAGE_KEY) return;
      applyIntent(readIntent(), false);
    }
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [applyIntent]);

  const value = useMemo<AppearanceContextValue>(
    () => ({ intent, resolved, setIntent }),
    [intent, resolved, setIntent]
  );

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
}
